import { createPackagePickerService } from '../../services';
import _ from 'lodash';
import { getSelectedComponentAppData as getSelectedComponentAppDataUtil } from '../utils/handlerUtils';
import { createWindowPlacementHandlers } from './windowPlacementHandlers';
import { createProductHandlers } from './productHandlers';
import { createComponentHandlers } from './componentHandlers';
import { createSettingsHandlers } from './settingsHandlers';

import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import type { DocumentServicesObject } from '@wix/document-services-types';
import type {
  TPAHandler,
  TPAMessage,
  TPAMessageCallback,
} from '../../types/tpaMessage';
import type {
  TpaStyleService,
  TpaMediaDialogService,
  TpaSettingsPanelService,
  AppRefreshService,
} from '../../services';
import { createStyleHandlers } from './styleHandlers';
import { createInstallationHandlers } from './installationHandlers';

interface AppEngagedMessageData {
  premiumIntent?: string;
}

export interface EditorTPAHandlers {
  getWindowPlacement: TPAHandler;
  setWindowPlacement: TPAHandler;
  isComponentInstalled: TPAHandler;
  isApplicationInstalled: TPAHandler;
  appEngaged: TPAHandler<AppEngagedMessageData>;
  addComponent: TPAHandler;
  setFullWidth: TPAHandler;
  isFullWidth: TPAHandler;
  openSiteMembersSettingsDialog: TPAHandler;
  getCurrentPageId: TPAHandler;
  openMediaDialog: TPAHandler;
  openLinkPanel: TPAHandler;
  settingsOpenModal: TPAHandler;
  getDashboardAppUrl: TPAHandler;
  postMessage: TPAHandler;
  refreshLivePreview: TPAHandler;
  refreshAppsOnDashboardClose: TPAHandler;
  migrate: TPAHandler;
  stylesReady: TPAHandler;
  getWixUpgradeUrl: TPAHandler;
  appIsAlive: TPAHandler;
  closeWindow: TPAHandler;
  getProducts: TPAHandler;
  openBillingPage: TPAHandler;
  openReviewInfo: TPAHandler;
  trackAppUpgrade: TPAHandler;
  setStyleParam: TPAHandler;
  setGlobalStyleParam: TPAHandler;
  setExternalId: TPAHandler;
  getExternalId: TPAHandler;
  getPermissions: TPAHandler;
}

export type TpaHandlerType = keyof EditorTPAHandlers;

interface TpaHandlersServicesDependencies {
  tpaSettingsPanelService: TpaSettingsPanelService;
  tpaMediaDialogService: TpaMediaDialogService;
  tpaStyleService: TpaStyleService;
}

export interface SettingsCompIdTracker {
  get: () => string | undefined;
  set: (compId: string) => void;
  clear: () => void;
}

export const createEditorTPAHandlers = (
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
  appRefreshService: AppRefreshService,
  tpaHandlersServices: TpaHandlersServicesDependencies,
): EditorTPAHandlers => {
  const { tpaSettingsPanelService, tpaMediaDialogService, tpaStyleService } =
    tpaHandlersServices;

  const getSelectedComponentAppData = () =>
    getSelectedComponentAppDataUtil(platformApiMethods, documentServicesAPI);

  let settingsCompId: string | undefined;
  const settingsCompIdTracker: SettingsCompIdTracker = {
    get: () => settingsCompId,
    set: (compId: string) => {
      settingsCompId = compId;
    },
    clear: () => {
      settingsCompId = undefined;
    },
  };

  const packagePickerService = createPackagePickerService(
    platformApiMethods,
    documentServicesAPI,
  );

  const installationHandlers = createInstallationHandlers(
    platformApiMethods,
    documentServicesAPI,
    getSelectedComponentAppData,
  );

  const windowPlacementHandlers = createWindowPlacementHandlers(
    platformApiMethods,
    documentServicesAPI,
  );

  const productHandlers = createProductHandlers(
    platformApiMethods,
    documentServicesAPI,
    packagePickerService,
    getSelectedComponentAppData,
  );

  const componentHandlers = createComponentHandlers(
    platformApiMethods,
    documentServicesAPI,
    tpaMediaDialogService,
    getSelectedComponentAppData,
    settingsCompIdTracker,
  );

  const settingsHandlers = createSettingsHandlers(
    platformApiMethods,
    documentServicesAPI,
    appRefreshService,
    tpaSettingsPanelService,
    getSelectedComponentAppData,
    settingsCompIdTracker,
  );

  const styleHandlers = createStyleHandlers(
    platformApiMethods,
    documentServicesAPI,
    tpaStyleService,
  );

  const setExternalId = (msg: TPAMessage, callback: TPAMessageCallback) => {
    if (!msg.compId) {
      throw new Error(
        '"compId" should be present in the message data for setExternalId handler',
      );
    }

    const compPointer = documentServicesAPI.components.get.byId(msg.compId);

    if (!compPointer) {
      throw new Error(
        `Component with id ${msg.compId} was not found in the document`,
      );
    }

    documentServicesAPI.tpa.comp.setExternalId(
      compPointer,
      msg.data?.externalId,
      callback,
      msg.data?.preventRefresh,
    );
  };

  const getExternalId = (msg: TPAMessage, callback: TPAMessageCallback) => {
    if (!msg.compId) {
      throw new Error(
        '"compId" should be present in the message data for getExternalId handler',
      );
    }

    const compPointer = documentServicesAPI.components.get.byId(msg.compId);

    if (!compPointer) {
      throw new Error(
        `Component with id ${msg.compId} was not found in the document`,
      );
    }

    const externalId = documentServicesAPI.tpa.comp.getExternalId(compPointer);
    callback(externalId);
  };

  const noopForBIEvents = async (
    _msg: TPAMessage,
    _cb: TPAMessageCallback,
  ) => {};

  return {
    ...windowPlacementHandlers,
    ...installationHandlers,
    ...componentHandlers,
    ...settingsHandlers,
    ...productHandlers,
    ...styleHandlers,
    setExternalId,
    getExternalId,
    getPermissions: noopForBIEvents,
  };
};
